import { Link } from "react-router-dom";
import { Row, Dropdown, Tooltip, Space } from "antd";
import { useTranslation, withTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { userInfoStore } from "Stores/UserInfoStore";
import { CustomerLogo, CustomerText } from "Components/Core/CustomerLogo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import i18n, { I18N_NS } from "Config/i18n";
import { headerStore } from "Stores/HeaderStore";
import { ItemType } from "antd/lib/menu/interface";
import { umami } from "General/Umami";
import { Pro4allLauncher } from "./Pro4AllLauncher";

const LightLogo = () => {
  return <div className="light-logo"> </div>;
};

const AdminButton = () => {
  if (userInfoStore.isVisibleFor(["ADMIN", "PRGROUPADMIN"])) {
    return (
      <Link to="/admin">
        <div className="btn-header">
          <FontAwesomeIcon icon={["fal", "cogs"]} size="2x" />
        </div>
      </Link>
    );
  } else {
    return null;
  }
};

const HeaderLanguageSwitcher = withTranslation(I18N_NS.LOGIN)(() => {
  const changeLanguage = (lang: "en" | "de") => {
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
  };
  return (
    <div className="header-ln">
      <a className={`${i18n.language === "de" ? "active" : ""}`} onClick={() => changeLanguage("de")}>
        DE
      </a>

      <a className={`${i18n.language === "en" ? "active" : ""}`} onClick={() => changeLanguage("en")}>
        EN
      </a>
    </div>
  );
});

/**
 * The Dummy Implementation of The HeaderLogin
 *
 * This Header is only Displayed when the user needs to Login
 */
const HeaderLogin = () => {
  return (
    <Row justify="space-between" align="middle">
      <CustomerLogo />
      <Space>
        <HeaderSupportIcon />
        <HeaderLanguageSwitcher />
      </Space>
    </Row>
  );
};

const MenuItemWithIcon = (props: { icon: string; label: React.ReactNode }) => (
  <div>
    <FontAwesomeIcon
      icon={["far", props.icon] as IconProp}
      style={{ fontSize: 16, marginRight: 10, color: "#818288" }}
    />
    {props.label}
  </div>
);

const HeaderGuestLanguageSwitcher = () => {
  return (
    <div className="flex flex-center-right">
      <HeaderLanguageSwitcher />
    </div>
  );
};

const HeaderUserIcon = () => {
  const iconStyle = userInfoStore.isUnifiedUser ? "fas" : "far";
  return <FontAwesomeIcon icon={[iconStyle, "user"]} size="lg" style={{ width: "2em" }} fixedWidth={true} />;
};

const HeaderSupportIcon = () => {
  const { t } = useTranslation(I18N_NS.GENERAL);
  return (
    <Link to="/support" onClick={() => umami.track("help_area", { action_item: "question_icon" })}>
      <div className="btn-header">
        <Tooltip title={t("Help")}>
          <FontAwesomeIcon icon={["fal", "question-circle"]} fixedWidth={true} size="2x" />
        </Tooltip>
      </div>
    </Link>
  );
};

const HeaderUser = observer(() => {
  const { t } = useTranslation(I18N_NS.GENERAL);
  const items: ItemType[] = [
    {
      key: "login",
      label: (
        <Link to="/login" onClick={() => userInfoStore.signOut("Logout", false)}>
          <MenuItemWithIcon icon="sign-out" label={t("Logout")} />
        </Link>
      ),
    },
    {
      key: "support",
      label: (
        <Link to="/support" onClick={() => umami.track("help_area", { action_item: "dropdown_link" })}>
          {t("Help")}
        </Link>
      ),
    },
  ];
  if (
    userInfoStore.allowedToChangePassword ||
    userInfoStore.allowedToChangeSetting ||
    userInfoStore.loginInfo?.unifiedAccount
  ) {
    items.unshift({
      key: "userSettings",
      label: (
        <Link to="/userSettings">
          <MenuItemWithIcon icon="user" label={t("Settings")} />
        </Link>
      ),
    });
    if (userInfoStore.isVisibleFor(["ADMIN", "PRGROUPADMIN"])) {
      items.unshift({
        key: "admin",
        label: (
          <Link to="/admin">
            <MenuItemWithIcon icon="cogs" label={t("Administration")} />
          </Link>
        ),
      });
    }
  }

  return (
    <div style={{ display: "flex" }}>
      <CustomerLogo />
      <CustomerText />
      {userInfoStore.isGuest && <HeaderGuestLanguageSwitcher />}
      <nav className="header-menu">
        <ul>
          <li>
            <HeaderSupportIcon />
          </li>
          <li>
            <Pro4allLauncher />
          </li>
          <li>
            <Dropdown menu={{ items }} trigger={["click"]} styles={{ root: { position: "fixed", maxWidth: "172px" } }}>
              <div className="btn-header">
                {/* eslint-disable-next-line max-len */}
                <HeaderUserIcon />{" "}
                <span className="btn-header-username" title={userInfoStore.name}>
                  {userInfoStore.name}
                </span>
                <FontAwesomeIcon icon={["fas", "angle-down"]} fixedWidth={true} />
              </div>
            </Dropdown>
          </li>
          <li>
            <AdminButton />
          </li>
          {headerStore.logo ? (
            <li style={{ marginLeft: 10 }}>
              {" "}
              <LightLogo />
            </li>
          ) : null}
        </ul>
      </nav>
    </div>
  );
});

export const Header = observer(() => {
  if (userInfoStore.isAuthenticated) {
    return <HeaderUser />;
  } else {
    return <HeaderLogin />;
  }
});
